/**
 * v2 NeedsMoreInfoQuestion. Talks to the deck controller via the per-card
 * coordination context.
 *
 * Answering is button-driven. It used to be a swipe (left = no, right = yes),
 * but swipe-left is now the deck-wide skip gesture, so the two would collide.
 * The buttons keep the old geography — No on the left, Yes on the right, in
 * the row the swipe hints used to occupy — so users who built muscle memory
 * for the gesture still reach for the right side to say yes.
 *
 * Flow:
 *   1. User taps Yes or No. `deck.beginSwipePending(direction)` snapshots the
 *      card definition (so an engine drop mid-flight can't yank it) and holds
 *      the wrapper at its idle pose.
 *   2. The full-card `isSubmitting` overlay covers the round-trip.
 *   3. Mutation resolves. Handler calls `deck.resolveSwipe("committed")` →
 *      status becomes `exit-complete`. The deck wrapper fades the card in
 *      place; back cards' indices have already shifted so they spring up.
 *   4. On rejection, `deck.resolveSwipe("rejected")` returns the card to idle
 *      and `ErrorHandler.default` surfaces a toast. Nothing is rethrown —
 *      there's no gesture left to spring back.
 */
import { useState } from "react";

import { Button } from "@/web/components/primitives/button";
import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { Spinner } from "@/web/components/primitives/spinner";
import { Textarea } from "@/web/components/primitives/textarea";
import { ErrorHandler } from "@/web/lib/errors";
import { cn } from "@/web/lib/styling";
import { useProcedureMutation } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import { CATEGORY_PALETTES } from "@/web/routes/_protected/smb/-dashboard/-categoryPalettes";
import { useDeckCardCoordination } from "@/web/routes/_protected/smb/-dashboard/-DeckContext";
import { QuestionDetailsContent } from "@/web/routes/_protected/smb/-dashboard/-questions/QuestionDetailsContent";
import { QuestionSubtitleMarkdown } from "@/web/routes/_protected/smb/-dashboard/-questions/QuestionSubtitleMarkdown";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
  SmbQuestionDetails,
} from "@/web/routes/_protected/smb/-dashboard/-types";

const BUDGET_CARD_PALETTE = CATEGORY_PALETTES.budget;

export type NeedsMoreInfoQuestionResult = {
  answer: boolean;
  note?: string;
};

export type NeedsMoreInfoQuestionProps = {
  questionId: string;
  badge: string;
  question: string;
  subtitle?: string;
  details?: SmbQuestionDetails;
};

function AnswerButton({
  answer,
  disabled,
  onAnswer,
}: {
  answer: boolean;
  disabled: boolean;
  onAnswer: (answer: boolean) => void;
}) {
  return (
    <Button
      variant="solid"
      color={BUDGET_CARD_PALETTE.primaryButtonColor}
      size="lg"
      data-slot={answer ? "smb-action-card-answer-yes" : "smb-action-card-answer-no"}
      className={cn("min-w-24 flex-1", BUDGET_CARD_PALETTE.primaryButtonClass)}
      disabled={disabled}
      onClick={() => onAnswer(answer)}
    >
      {answer ? "Yes" : "No"}
    </Button>
  );
}

function NeedsMoreInfoQuestion({
  questionId,
  badge,
  question,
  subtitle,
  details,
  onSkip,
  onComplete,
}: NeedsMoreInfoQuestionProps & SmbActionCoreProps) {
  const [note, setNote] = useState("");
  const [noteDrawerOpen, setNoteDrawerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const coordination = useDeckCardCoordination();

  const { mutateAsync: submitAnswer } = useProcedureMutation(
    procedures.submitUnderwritingQuestionAnswer,
  );

  const handleAnswer = async (answer: boolean) => {
    if (isSubmitting) return;
    coordination?.beginSwipePending(answer ? "right" : "left");
    setIsSubmitting(true);
    const trimmedNote = note.trim();
    try {
      await submitAnswer({
        answer: {
          tag: "NeedsMoreInfo",
          value: { value: answer, note: trimmedNote || undefined },
        },
        questionId,
      });
      coordination?.resolveSwipe("committed");
      // Notify the action contract too — preserves API parity with v1 for
      // any future consumer that wants to observe completion.
      onComplete({
        answer,
        note: trimmedNote || undefined,
      } satisfies NeedsMoreInfoQuestionResult);
    } catch (error) {
      coordination?.resolveSwipe("rejected");
      ErrorHandler.default(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/*
       * No `swipe` prop: this card takes the deck's universal
       * swipe-left-to-skip from `ActionCard`. Answering is the button pair
       * below.
       */}
      <ActionCard
        category="budget"
        aria-busy={isSubmitting}
        swipeDisabled={noteDrawerOpen || isSubmitting}
      >
        <ActionCard.Header>
          <ActionCard.Badge className="bg-withus-white/5 text-withus-white">
            {badge}
          </ActionCard.Badge>
          {details && <ActionCard.DetailsTrigger />}
        </ActionCard.Header>
        <ActionCard.Main>
          <ActionCard.Title>{question}</ActionCard.Title>
          {subtitle && (
            <ActionCard.Description>
              <QuestionSubtitleMarkdown markdown={subtitle} />
            </ActionCard.Description>
          )}
        </ActionCard.Main>
        {/* No left / Yes right — the geography the swipe hints used to teach. */}
        <ActionCard.Body className="flex items-center justify-center gap-3 overflow-hidden px-3 pt-8">
          <AnswerButton answer={false} disabled={isSubmitting} onAnswer={handleAnswer} />
          <AnswerButton answer={true} disabled={isSubmitting} onAnswer={handleAnswer} />
        </ActionCard.Body>
        <ActionCard.Footer>
          <ActionCard.SkipButton onSkip={isSubmitting ? undefined : onSkip} />
          <Button
            variant="outline"
            color={BUDGET_CARD_PALETTE.skipButtonColor}
            size="md"
            data-slot="smb-action-card-drawer-trigger"
            className={BUDGET_CARD_PALETTE.skipButtonClass}
            disabled={isSubmitting}
            aria-expanded={noteDrawerOpen}
            aria-haspopup="dialog"
            onClick={() => setNoteDrawerOpen(true)}
          >
            Add note
          </Button>
        </ActionCard.Footer>
        {isSubmitting ? (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-overlay-35 backdrop-blur-sm">
            <div
              role="status"
              aria-live="polite"
              className="flex items-center gap-2 text-sm text-withus-white"
            >
              <Spinner size="small" className="text-withus-white" />
              <span>Saving…</span>
            </div>
          </div>
        ) : null}
        {details && (
          <ActionCard.Back
            title={details.title}
            description={
              details.subtitle ? (
                <QuestionSubtitleMarkdown markdown={details.subtitle} />
              ) : undefined
            }
          >
            <QuestionDetailsContent details={details} />
          </ActionCard.Back>
        )}
      </ActionCard>

      <Drawer open={noteDrawerOpen} onOpenChange={setNoteDrawerOpen}>
        <DrawerContent className={cn(BUDGET_CARD_PALETTE.cardBg, BUDGET_CARD_PALETTE.onCardFg)}>
          <DrawerHeader>
            <DrawerTitle>Add a note</DrawerTitle>
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody className="flex min-h-0 flex-col gap-3">
            <Textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Add a note to include with your response"
              aria-label="Add a note to include with your response"
              disabled={isSubmitting}
              className="min-h-31 resize-none border-withus-white/30 bg-white/5 p-4 text-withus-white placeholder:text-withus-white/40 focus-visible:border-withus-white focus-visible:ring-0"
            />
          </DrawerBody>
          <DrawerFooter>
            <Button
              variant="solid"
              color={BUDGET_CARD_PALETTE.primaryButtonColor}
              size="md"
              disabled={isSubmitting}
              className={cn("w-full", BUDGET_CARD_PALETTE.primaryButtonClass)}
              onClick={() => setNoteDrawerOpen(false)}
            >
              Done
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}

export function createNeedsMoreInfoQuestion(
  props: NeedsMoreInfoQuestionProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = props.questionId, canSkip = true, ...rest } = props;
  return {
    id,
    category: "budget",
    canSkip,
    render: (core) => <NeedsMoreInfoQuestion {...rest} {...core} />,
  };
}
